import { mapMovementObstacles, stepCrossesWalls } from '../../../shared/mapLayout.mjs';

const SIZE_SQUARES = Object.freeze({ tiny: 1, small: 1, medium: 1, large: 2, huge: 3, gargantuan: 4 });
export const SIZE_RANK = Object.freeze({ tiny: 0, small: 1, medium: 2, large: 3, huge: 4, gargantuan: 5 });

const finite = (value) => value == null || value === '' ? null : Number.isFinite(Number(value)) ? Number(value) : null;

const DEFAULT_GRID_DIMENSION = 24;
const parsePositiveNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const match = value.match(/([0-9]*\.?[0-9]+)/);
    if (match) {
      const parsed = Number.parseFloat(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  return null;
};

const parseGridDimensionsFromValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    const [first, second] = value;
    const firstNumber = parsePositiveNumber(first);
    const secondNumber = parsePositiveNumber(second);

    if (firstNumber !== null || secondNumber !== null) {
      const resolvedFirst = firstNumber ?? secondNumber;
      const resolvedSecond = secondNumber ?? firstNumber;
      if (resolvedFirst !== null) {
        return {
          columns: Math.max(1, resolvedFirst),
          rows: Math.max(1, resolvedSecond ?? resolvedFirst),
        };
      }
    }

    return null;
  }

  const numericValue = parsePositiveNumber(value);
  if (numericValue !== null) {
    const safeValue = Math.max(1, numericValue);
    return { columns: safeValue, rows: safeValue };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const pairMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (pairMatch) {
      const [, columnMatch, rowMatch] = pairMatch;
      const parsedColumns = Number.parseFloat(columnMatch);
      const parsedRows = Number.parseFloat(rowMatch);
      if (Number.isFinite(parsedColumns) && Number.isFinite(parsedRows)) {
        return {
          columns: Math.max(1, parsedColumns),
          rows: Math.max(1, parsedRows),
        };
      }
    }
  }

  return null;
};

export const getMapGridDimensions = (map) => {
  if (!map || typeof map !== 'object') {
    return { columns: DEFAULT_GRID_DIMENSION, rows: DEFAULT_GRID_DIMENSION };
  }

  const assignDimension = (current, value) => {
    const parsed = parsePositiveNumber(value);
    if (parsed === null) {
      return current;
    }

    const safeValue = Math.max(1, parsed);
    return current ?? safeValue;
  };

  let columns = null;
  let rows = null;

  const gridLikeObjects = [map.grid, map.meta, map.metadata, map.settings, map.details];
  const directColumnCandidates = [
    map.gridColumns,
    map.gridWidth,
    map.columns,
    map.widthSquares,
    map.width,
  ];
  const directRowCandidates = [
    map.gridRows,
    map.gridHeight,
    map.rows,
    map.heightSquares,
    map.height,
  ];

  directColumnCandidates.forEach((candidate) => {
    columns = assignDimension(columns, candidate);
  });
  directRowCandidates.forEach((candidate) => {
    rows = assignDimension(rows, candidate);
  });

  gridLikeObjects.forEach((obj) => {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    columns = assignDimension(columns, obj.columns ?? obj.cols ?? obj.width);
    rows = assignDimension(rows, obj.rows ?? obj.height);

    if (columns !== null && rows !== null) {
      return;
    }

    const dimensionsValue =
      obj.dimensions ?? obj.size ?? obj.gridSize ?? obj.gridDimensions ?? obj.shape;
    const parsedDimensions = parseGridDimensionsFromValue(dimensionsValue);
    if (parsedDimensions) {
      if (columns === null && parsedDimensions.columns) {
        columns = parsedDimensions.columns;
      }
      if (rows === null && parsedDimensions.rows) {
        rows = parsedDimensions.rows;
      }
    }
  });

  const fallbackDimensionStrings = [
    map.gridSize,
    map.gridDimensions,
    map.dimensions,
    map.size,
    map.mapSize,
  ];

  fallbackDimensionStrings.forEach((value) => {
    if (columns !== null && rows !== null) {
      return;
    }

    const parsed = parseGridDimensionsFromValue(value);
    if (!parsed) {
      return;
    }

    if (columns === null && parsed.columns) {
      columns = parsed.columns;
    }
    if (rows === null && parsed.rows) {
      rows = parsed.rows;
    }
  });

  const safeColumns = columns ?? DEFAULT_GRID_DIMENSION;
  const safeRows = rows ?? safeColumns;

  return {
    columns: Math.max(1, safeColumns),
    rows: Math.max(1, safeRows),
  };
};



export const normalizeCreatureSize = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(SIZE_RANK, normalized) ? normalized : null;
};

export const getCreatureSizeRank = (value) => {
  const size = normalizeCreatureSize(value);
  return size == null ? null : SIZE_RANK[size];
};

export const getFootprintSquares = (value) => SIZE_SQUARES[normalizeCreatureSize(value) || 'medium'];

/** Minimum occupied-cell distance using RealmTracker's Chebyshev grid rule. */
export const minimumFootprintDistanceFeet = ({ sourceToken, sourceSize, targetToken, targetSize, map }) => {
  const source = tokenAnchorCell(sourceToken, map);
  const target = tokenAnchorCell(targetToken, map);
  if (!source || !target) return null;
  const sourceWidth = getFootprintSquares(sourceSize);
  const targetWidth = getFootprintSquares(targetSize);
  const axisGap = (a, aWidth, b, bWidth) => Math.max(0, b - (a + aWidth - 1), a - (b + bWidth - 1));
  return Math.max(
    axisGap(source.x, sourceWidth, target.x, targetWidth),
    axisGap(source.y, sourceWidth, target.y, targetWidth),
  ) * 5;
};

export const tokenAnchorCell = (token, map) => {
  if (!token) return null;
  const { columns, rows } = getMapGridDimensions(map);
  const x = finite(token.gridX ?? token.column ?? token.col);
  const y = finite(token.gridY ?? token.row);
  const normalizedX = finite(token.x ?? token.position?.x);
  const normalizedY = finite(token.y ?? token.position?.y);
  if (x == null && normalizedX == null) return null;
  if (y == null && normalizedY == null) return null;
  return {
    x: Math.floor(x ?? normalizedX * columns),
    y: Math.floor(y ?? normalizedY * rows),
  };
};

export const footprintCenter = (anchor, size) => ({
  x: anchor.x + (getFootprintSquares(size) / 2),
  y: anchor.y + (getFootprintSquares(size) / 2),
});

/**
 * Quantize a center-to-center ray to RealmTracker's eight Chebyshev directions.
 * A direction is diagonal unless one component is at least twice the other;
 * ties therefore remain diagonal and the result is deterministic.
 */
export const straightAwayDirection = ({ sourceAnchor, sourceSize, targetAnchor, targetSize }) => {
  if (!sourceAnchor || !targetAnchor) return null;
  const source = footprintCenter(sourceAnchor, sourceSize);
  const target = footprintCenter(targetAnchor, targetSize);
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  if (dx === 0 && dy === 0) return null;
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  if (absX >= absY * 2) return { x: Math.sign(dx), y: 0 };
  if (absY >= absX * 2) return { x: 0, y: Math.sign(dy) };
  return { x: Math.sign(dx), y: Math.sign(dy) };
};

export const footprintWithinBounds = (anchor, size, map) => {
  if (!anchor) return false;
  const { columns, rows } = getMapGridDimensions(map);
  const footprint = getFootprintSquares(size);
  return Number.isInteger(anchor.x) && Number.isInteger(anchor.y) && anchor.x >= 0 && anchor.y >= 0 &&
    anchor.x + footprint <= columns && anchor.y + footprint <= rows;
};

export const anchorToNormalizedPosition = (anchor, map) => {
  const { columns, rows } = getMapGridDimensions(map);
  return { x: (anchor.x + 0.5) / columns, y: (anchor.y + 0.5) / rows };
};

export const getPushDestination = ({ sourceToken, sourceSize, targetToken, targetSize, distanceFeet, map, maximumDistanceFeet=10 }) => {
  const steps = Number(distanceFeet) / 5;
  if (!Number.isInteger(steps) || steps < 1 || steps > maximumDistanceFeet/5) return null;
  const sourceAnchor = tokenAnchorCell(sourceToken, map);
  const targetAnchor = tokenAnchorCell(targetToken, map);
  const direction = straightAwayDirection({ sourceAnchor, sourceSize, targetAnchor, targetSize });
  if (!direction) return null;
  // Walls, windows and solid blocks drawn on the map stop a push like the map's edge does.
  const { columns, rows } = getMapGridDimensions(map);
  const obstacles = mapMovementObstacles(map, columns, rows);
  const edges = new Set(obstacles?.walls || []);
  const blocked = new Set((obstacles?.blocked || []).map((cell) => `${cell.x},${cell.y}`));
  const width = getFootprintSquares(targetSize);
  const footprintAt = (anchor) => Array.from({ length: width * width }, (_, i) => ({ x: anchor.x + (i % width), y: anchor.y + Math.floor(i / width) }));
  for (let step = 1; step <= steps; step += 1) {
    const from = { x: targetAnchor.x + direction.x * (step - 1), y: targetAnchor.y + direction.y * (step - 1) };
    const traversed = { x: targetAnchor.x + direction.x * step, y: targetAnchor.y + direction.y * step };
    if (!footprintWithinBounds(traversed, targetSize, map)) return null;
    if (stepCrossesWalls(edges, footprintAt(from), direction.x, direction.y)) return null;
    if (footprintAt(traversed).some((cell) => blocked.has(`${cell.x},${cell.y}`))) return null;
  }
  const anchor = { x: targetAnchor.x + direction.x * steps, y: targetAnchor.y + direction.y * steps };
  return { anchor, direction, position: anchorToNormalizedPosition(anchor, map), distanceFeet: steps * 5 };
};

export const getLegalPushDestinations = (input) => [5, 10]
  .map((distanceFeet) => getPushDestination({ ...input, distanceFeet }))
  .filter(Boolean);

